import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../supabase';
import { useNavigate } from 'react-router-dom';
import { DEVELLO_API_ORIGIN } from '../utils/asyncReliability';
import ThemeToggle from '../components/ThemeToggle';
import ProjectDetailWorkspace from '../components/projects/ProjectDetailWorkspace';
import { ensureProjectExtensions, getBudgetPercentUsed, getProfitHealthVariant } from '../utils/projectUtils';

// ── Copied from Projects.js (same visual style) ───────────────
const STATUS_BADGE_CLASS = {
  pending:   'bg-amber-100 text-amber-900',
  active:    'bg-emerald-100 text-emerald-800',
  completed: 'bg-blue-100 text-blue-800',
};
const STATUS_DISPLAY_LABELS = {
  pending:   'Pending',
  active:    'Active',
  completed: 'Completed',
};

function getDateStatus(project) {
  if (!project) return 'active';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = project.startDate ? new Date(project.startDate) : null;
  const end   = project.deadline  ? new Date(project.deadline)  : null;
  if (start && today < start) return 'pending';
  if (end   && today > end)   return 'completed';
  return 'active';
}

function BudgetStrip({ percent, variant }) {
  const color = variant === 'danger' ? 'bg-red-500' : variant === 'warning' ? 'bg-amber-400' : 'bg-emerald-500';
  return (
    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
      <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${Math.min(percent, 100)}%` }} />
    </div>
  );
}

const formatProjectDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return d; }
};

// Permission key → workspace tab id
const PERM_TO_TAB = {
  daily_logs:    'logs',
  change_orders: 'changeOrders',
  photos:        'gallery',
  vault:         'vault',
  expenses:      'expenses',
};

// ── Settings view (email + password change) ────────────────────
function SettingsView() {
  const inputClass = 'w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20';
  const labelClass = 'block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5';

  // ── Email change ───────────────────────────────────────────
  const [currentEmail, setCurrentEmail] = useState('');
  const [newEmail, setNewEmail]         = useState('');
  const [emailSaving, setEmailSaving]   = useState(false);
  const [emailMsg, setEmailMsg]         = useState('');
  const [emailIsErr, setEmailIsErr]     = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user?.email) { setCurrentEmail(user.email); setNewEmail(user.email); }
    });
  }, []);

  const handleEmailSave = async (e) => {
    e.preventDefault();
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed.includes('@')) { setEmailIsErr(true); setEmailMsg('Enter a valid email.'); return; }
    if (trimmed === currentEmail.toLowerCase()) { setEmailIsErr(false); setEmailMsg('That is already your email.'); return; }
    setEmailSaving(true); setEmailMsg(''); setEmailIsErr(false);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(`${DEVELLO_API_ORIGIN}/api/team/change-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ email: trimmed }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json.error || 'Failed to update email.');
      setCurrentEmail(trimmed);
      setEmailIsErr(false);
      setEmailMsg('Email updated. Use your new email to sign in next time.');
    } catch (err) { setEmailIsErr(true); setEmailMsg(err.message); }
    finally { setEmailSaving(false); }
  };

  // ── Password change ────────────────────────────────────────
  const [currentPw, setCurrentPw]   = useState('');
  const [newPw, setNewPw]           = useState('');
  const [confirmPw, setConfirmPw]   = useState('');
  const [pwSaving, setPwSaving]     = useState(false);
  const [pwSuccess, setPwSuccess]   = useState('');
  const [pwError, setPwError]       = useState('');

  const handlePwSave = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) { setPwError('New password must be at least 6 characters.'); return; }
    if (newPw !== confirmPw) { setPwError('Passwords do not match.'); return; }
    setPwSaving(true); setPwError(''); setPwSuccess('');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error: signInErr } = await supabase.auth.signInWithPassword({ email: user?.email, password: currentPw });
      if (signInErr) throw new Error('Current password is incorrect.');
      const { error: updateErr } = await supabase.auth.updateUser({ password: newPw });
      if (updateErr) throw updateErr;
      setPwSuccess('Password updated successfully.');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) { setPwError(err.message); }
    finally { setPwSaving(false); }
  };

  return (
    <div className="max-w-lg space-y-6">
      <div>
        <h1 className="text-xl font-bold text-slate-900 tracking-tight">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Manage your account preferences.</p>
      </div>

      {/* Email */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
        <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
          <h3 className="text-sm font-semibold text-slate-900">Email Address</h3>
          <p className="text-xs text-slate-500 mt-0.5">Your contractor will see your updated email.</p>
        </div>
        <form className="p-6 space-y-4" onSubmit={handleEmailSave}>
          <div>
            <label className={labelClass}>Current Email</label>
            <p className="text-sm text-slate-600 font-medium">{currentEmail || '—'}</p>
          </div>
          <div>
            <label className={labelClass}>New Email</label>
            <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className={inputClass} placeholder="new@email.com" required />
          </div>
          {emailMsg && <p className={`text-sm rounded-xl px-4 py-3 border ${emailIsErr ? 'text-red-600 bg-red-50 border-red-100' : 'text-emerald-700 bg-emerald-50 border-emerald-100'}`}>{emailMsg}</p>}
          <button type="submit" disabled={emailSaving} className="w-full rounded-xl bg-primary py-3 text-sm font-bold text-white shadow-md shadow-primary/20 hover:bg-primary-dark disabled:opacity-60 transition-colors">
            {emailSaving ? 'Saving…' : 'Update Email'}
          </button>
        </form>
      </div>

      {/* Appearance */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
        <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
          <h3 className="text-sm font-semibold text-slate-900">Appearance</h3>
          <p className="text-xs text-slate-500 mt-0.5">Optional dark theme for the team portal</p>
        </div>
        <div className="p-6">
          <ThemeToggle />
        </div>
      </div>

      {/* Password */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
        <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
          <h3 className="text-sm font-semibold text-slate-900">Change Password</h3>
        </div>
        <form className="p-6 space-y-4" onSubmit={handlePwSave}>
          <div><label className={labelClass}>Current Password</label><input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} className={inputClass} placeholder="••••••••" required /></div>
          <div><label className={labelClass}>New Password</label><input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className={inputClass} placeholder="••••••••" required /></div>
          <div><label className={labelClass}>Confirm New Password</label><input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className={inputClass} placeholder="••••••••" required /></div>
          {pwError   && <p className="text-red-600 text-sm rounded-xl bg-red-50 border border-red-100 px-4 py-3">{pwError}</p>}
          {pwSuccess && <p className="text-emerald-700 text-sm rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3">{pwSuccess}</p>}
          <button type="submit" disabled={pwSaving} className="w-full rounded-xl bg-primary py-3 text-sm font-bold text-white shadow-md shadow-primary/20 hover:bg-primary-dark disabled:opacity-60 transition-colors">
            {pwSaving ? 'Saving…' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Sidebar nav item ───────────────────────────────────────────
function SideNavItem({ active, onClick, icon, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all text-left ${
        active
          ? 'bg-primary text-white shadow-lg shadow-primary/30 ring-1 ring-white/10'
          : 'text-slate-400 hover:bg-white/5 hover:text-white'
      }`}
    >
      <span className="h-5 w-5 shrink-0 opacity-90">{icon}</span>
      {children}
    </button>
  );
}

// ── Main dashboard ─────────────────────────────────────────────
const TeamMemberDashboard = () => {
  const navigate    = useNavigate();
  const sessionRef  = useRef(null);

  const [session,        setSession]        = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState('');
  const [member,         setMember]         = useState(null);
  const [contractorName, setContractorName] = useState('');
  const [projects,       setProjects]       = useState([]);
  const [permissions,    setPermissions]    = useState({});

  // 'projects' | 'settings'
  const [view,            setView]           = useState('projects');
  const [detailProjectId, setDetailProjectId] = useState(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // ── Load data ────────────────────────────────────────────────
  const loadData = useCallback(async (token) => {
    const resp = await fetch(`${DEVELLO_API_ORIGIN}/api/team/me`, { headers: { Authorization: `Bearer ${token}` } });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data.error || 'Failed to load data');
    return data;
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session: sess } }) => {
      if (!sess) { navigate('/account?mode=team'); return; }
      sessionRef.current = sess;
      setSession(sess);
      try {
        const data = await loadData(sess.access_token);
        setMember(data.member);
        setContractorName(data.contractorName);
        setProjects(data.projects || []);
        setPermissions(data.permissions || {});
      } catch (e) {
        if (e.message?.includes('deactivated') || e.message?.includes('not found')) {
          await supabase.auth.signOut();
          navigate('/account?mode=team');
        } else { setError(e.message); }
      } finally { setLoading(false); }
    });
  }, [navigate, loadData]);

  // ── Live sync: poll every 20 s for contractor-side changes ───────
  useEffect(() => {
    const token = sessionRef.current?.access_token;
    if (!token) return;
    const id = setInterval(async () => {
      try {
        const data = await loadData(sessionRef.current?.access_token || token);
        if (!data?.projects) return;
        setProjects((prev) => {
          const serverMap = new Map(data.projects.map((p) => [String(p.id), p]));
          return prev.map((local) => {
            const server = serverMap.get(String(local.id));
            if (!server) return local;
            const serverStr = JSON.stringify(server);
            const localStr  = JSON.stringify(local);
            return serverStr !== localStr ? { ...server } : local;
          });
        });
        if (data.permissions) setPermissions(data.permissions);
      } catch { /* silent — don't disrupt UI on poll failure */ }
    }, 20000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadData, session]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/account?mode=team');
  };

  // ── Helpers ───────────────────────────────────────────────────
  const getPermittedTabIds = useCallback((projectId) => {
    const perms = permissions[String(projectId)] || {};
    const permitted = [];
    const hasLegacyOverview = perms.overview === undefined
      && Object.keys(PERM_TO_TAB).some((key) => perms[key]?.view);
    if (perms.overview?.view || hasLegacyOverview) permitted.push('overview');
    for (const [permKey, tabId] of Object.entries(PERM_TO_TAB)) {
      if (perms[permKey]?.view) permitted.push(tabId);
    }
    return permitted;
  }, [permissions]);

  const getProjectTotalExpenses = useCallback((projectId) => {
    const p = projects.find((pr) => String(pr.id) === String(projectId));
    return (p?.expenses || []).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  }, [projects]);

  // ── Team patch: intercept workspace mutations ─────────────────
  // onPatchProject signature: (projectId, updater: project → project)
  const handleTeamPatch = useCallback((projectId, updater) => {
    setProjects((prev) => {
      const prevProject = prev.find((p) => String(p.id) === String(projectId));
      if (!prevProject) return prev;
      const nextProject = updater(ensureProjectExtensions(prevProject));

      // Diff arrays and call team API for the change
      const sections = [
        ['dailySiteLogs', 'daily_logs'],
        ['changeOrders',  'change_orders'],
        ['sitePhotos',    'photos'],
        ['vaultDocuments','vault'],
      ];
      for (const [field, apiSection] of sections) {
        const prevArr = prevProject[field] || [];
        const nextArr = nextProject[field] || [];
        if (nextArr.length > prevArr.length) {
          const added = nextArr.find((n) => !prevArr.some((p) => String(p.id) === String(n.id)));
          if (added) {
            fetch(`${DEVELLO_API_ORIGIN}/api/team/project/${projectId}/patch`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionRef.current?.access_token}` },
              body: JSON.stringify({ section: apiSection, action: 'add', entry: added }),
            }).catch(console.error);
          }
        } else if (nextArr.length < prevArr.length) {
          const deleted = prevArr.find((p) => !nextArr.some((n) => String(n.id) === String(p.id)));
          if (deleted) {
            fetch(`${DEVELLO_API_ORIGIN}/api/team/project/${projectId}/patch`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionRef.current?.access_token}` },
              body: JSON.stringify({ section: apiSection, action: 'delete', entry: { id: deleted.id } }),
            }).catch(console.error);
          }
        }
      }

      return prev.map((p) => String(p.id) === String(projectId) ? nextProject : p);
    });
  }, []);

  // ── Expense overrides for team members ────────────────────────
  const teamSaveExpense = useCallback(async (expenseData) => {
    const memberName = member?.full_name || member?.name || member?.email || null;
    const entry = {
      id:          Date.now().toString(),
      title:       expenseData.title,
      amount:      parseFloat(expenseData.amount) || 0,
      category:    expenseData.category,
      date:        expenseData.date,
      description: expenseData.description || '',
      projectId:   String(expenseData.projectId),
      createdAt:   new Date().toISOString(),
      receipts:    expenseData.receipts || [],
      laborOrigin: expenseData.laborOrigin || 'internal',
      ...(memberName ? { addedByName: memberName } : {}),
    };
    const resp = await fetch(`${DEVELLO_API_ORIGIN}/api/team/project/${expenseData.projectId}/patch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionRef.current?.access_token}` },
      body: JSON.stringify({ section: 'expenses', action: 'add', entry }),
    });
    if (!resp.ok) { const j = await resp.json(); throw new Error(j.error || 'Failed to save expense'); }
    setProjects((prev) => prev.map((p) =>
      String(p.id) === String(expenseData.projectId)
        ? { ...p, expenses: [...(p.expenses || []), entry] }
        : p
    ));
    return true;
  }, [member]);

  const teamUpdateExpense = useCallback(async (expenseData) => {
    if (!detailProjectId) return;
    const entry = {
      id:          expenseData.id,
      title:       expenseData.title,
      amount:      parseFloat(expenseData.amount) || 0,
      category:    expenseData.category,
      date:        expenseData.date,
      description: expenseData.description || '',
      editHistory: expenseData.editHistory || [],
    };
    const resp = await fetch(`${DEVELLO_API_ORIGIN}/api/team/project/${detailProjectId}/patch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionRef.current?.access_token}` },
      body: JSON.stringify({ section: 'expenses', action: 'update', entry }),
    });
    if (!resp.ok) {
      const j = await resp.json();
      throw new Error(j.error || 'Failed to update expense');
    }
    setProjects((prev) => prev.map((p) =>
      String(p.id) === String(detailProjectId)
        ? { ...p, expenses: (p.expenses || []).map((e) => String(e.id) === String(entry.id) ? { ...e, ...entry } : e) }
        : p
    ));
  }, [detailProjectId]);

  const teamDeleteExpense = useCallback(async (expenseId) => {
    if (!detailProjectId) return;
    const resp = await fetch(`${DEVELLO_API_ORIGIN}/api/team/project/${detailProjectId}/patch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionRef.current?.access_token}` },
      body: JSON.stringify({ section: 'expenses', action: 'delete', entry: { id: String(expenseId) } }),
    });
    if (!resp.ok) { const j = await resp.json(); throw new Error(j.error || 'Failed to delete expense'); }
    setProjects((prev) => prev.map((p) =>
      String(p.id) === String(detailProjectId)
        ? { ...p, expenses: (p.expenses || []).filter((e) => String(e.id) !== String(expenseId)) }
        : p
    ));
  }, [detailProjectId]);

  // ── Expense data for the open project ─────────────────────────
  const detailProjectExpenses = useMemo(() => {
    if (!detailProjectId) return [];
    const p = projects.find((pr) => String(pr.id) === String(detailProjectId));
    return (p?.expenses || []).map((e) => ({ ...e, projectId: String(detailProjectId) }));
  }, [projects, detailProjectId]);

  // ── Loading / error states ────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading workspace…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 shadow-xl text-center max-w-md">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={handleSignOut} className="text-primary font-medium text-sm hover:underline">Sign Out</button>
        </div>
      </div>
    );
  }

  const initial = (member?.full_name || member?.email || '?')[0].toUpperCase();

  // ── Sidebar ───────────────────────────────────────────────────
  const Sidebar = (
    <aside className="flex h-full flex-col border-r border-slate-800/90 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 shadow-[4px_0_24px_-4px_rgba(0,0,0,0.35)]">
      {/* Brand */}
      <div className="flex shrink-0 items-center border-b border-white/10 px-4 py-4">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">Devello</p>
          <p className="mt-0.5 bg-gradient-to-r from-cyan-300 via-white to-cyan-200/90 bg-clip-text text-lg font-bold tracking-tight text-transparent">
            Team Portal
          </p>
          {contractorName && <p className="text-[11px] text-slate-500 mt-0.5 truncate">{contractorName}</p>}
        </div>
      </div>

      {/* Nav */}
      <nav className="min-h-0 flex-1 overflow-y-auto py-3 px-3 space-y-1">
        <SideNavItem
          active={view === 'projects'}
          onClick={() => { setView('projects'); setMobileSidebarOpen(false); }}
          icon={
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
          }
        >
          Projects
        </SideNavItem>
        <SideNavItem
          active={view === 'settings'}
          onClick={() => { setView('settings'); setMobileSidebarOpen(false); }}
          icon={
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          }
        >
          Settings
        </SideNavItem>
      </nav>

      {/* User card */}
      <div className="shrink-0 border-t border-white/10 p-3">
        <div className="rounded-2xl border border-slate-700/60 bg-slate-800/35 backdrop-blur-sm p-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-dark text-sm font-semibold text-white shadow-inner ring-1 ring-white/10">
              {initial}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-slate-300">{member?.full_name || member?.email}</p>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Team Member</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            className="mt-3 w-full rounded-xl border border-slate-600/80 py-2 text-center text-sm font-medium text-slate-300 transition-colors hover:border-red-500/40 hover:bg-red-950/30 hover:text-red-200"
          >
            Sign out
          </button>
        </div>
      </div>
    </aside>
  );

  // ── Project cards (same style as contractor's Projects.js) ────
  const ProjectCards = () => (
    <div>
      <div className="mb-6 flex flex-col gap-1">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Team Portal</p>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">Projects</h2>
        <p className="text-sm text-slate-500">{projects.length} project{projects.length !== 1 ? 's' : ''} shared with you</p>
      </div>

      {projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
          </div>
          <p className="text-slate-700 font-medium">No projects yet</p>
          <p className="text-sm text-slate-500 mt-1">Your contractor hasn't shared any projects with you yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 items-stretch">
          {[...projects].sort((a, b) => {
            const t = (p) => {
              const end = p.deadline ? new Date(p.deadline).getTime() : 0;
              const created = p.createdAt ? new Date(p.createdAt).getTime() : 0;
              return end || created || 0;
            };
            return t(b) - t(a);
          }).map((project) => {
            const p         = ensureProjectExtensions(project);
            const st        = getDateStatus(p);
            const badgeClass = STATUS_BADGE_CLASS[st] || STATUS_BADGE_CLASS.active;
            const totalSpent = getProjectTotalExpenses(project.id);
            const budgetNum  = parseFloat(project.budget) || 0;
            const pct        = budgetNum > 0 ? getBudgetPercentUsed(budgetNum, totalSpent) : 0;
            const healthVariant = budgetNum > 0 ? getProfitHealthVariant(pct) : 'safe';
            const initials   = (project.name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

            return (
              <div
                key={project.id}
                className="group flex flex-col rounded-3xl border border-slate-200/90 bg-white p-5 shadow-sm transition-all hover:shadow-lg hover:border-slate-300/90 hover:-translate-y-0.5"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-800 to-slate-700 text-xs font-bold text-white shadow-inner">
                    {initials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base font-bold text-slate-900 leading-snug line-clamp-2">{project.name}</h3>
                    <span className={`mt-1.5 inline-flex text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md ${badgeClass}`}>
                      {STATUS_DISPLAY_LABELS[st] || st}
                    </span>
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-sm text-slate-600 flex-1">
                  {project.client && (
                    <p className="flex items-center gap-2 text-sm text-slate-700">
                      <svg className="w-4 h-4 shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="truncate">{project.client}</span>
                    </p>
                  )}
                  {(project.startDate || project.deadline) && (
                    <p className="flex items-center gap-2 text-xs text-slate-500">
                      <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {project.startDate && <span>{formatProjectDate(project.startDate)}</span>}
                      {project.startDate && project.deadline && <span className="text-slate-300">→</span>}
                      {project.deadline && <span>{formatProjectDate(project.deadline)}</span>}
                    </p>
                  )}

                  {budgetNum > 0 ? (
                    <>
                      <div className="flex items-baseline justify-between text-xs text-slate-500 pt-1">
                        <span>Est. ${budgetNum.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                        <span className="font-semibold text-slate-800 tabular-nums">
                          ${totalSpent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} spent
                        </span>
                      </div>
                      <BudgetStrip percent={pct} variant={healthVariant} />
                    </>
                  ) : (
                    <div className="rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600">
                      <span className="font-medium text-slate-700">Spend </span>
                      <span className="tabular-nums font-semibold">
                        ${totalSpent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                </div>

                {project.description && (
                  <p className="mt-3 line-clamp-2 rounded-xl bg-slate-50/80 px-3 py-2 text-[11px] leading-relaxed text-slate-600 border border-slate-100">
                    {project.description}
                  </p>
                )}

                <div className="mt-5">
                  <button
                    type="button"
                    onClick={() => setDetailProjectId(String(project.id))}
                    className="w-full rounded-2xl bg-primary py-2.5 text-sm font-semibold text-white hover:bg-primary-dark transition-colors shadow-sm shadow-primary/25"
                  >
                    Open workspace
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950">
      {/* Desktop sidebar */}
      <div className="fixed inset-y-0 left-0 z-40 w-[17rem] hidden md:block">
        {Sidebar}
      </div>

      {/* Mobile sidebar */}
      {mobileSidebarOpen && (
        <>
          <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={() => setMobileSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 z-50 w-[17rem] md:hidden">{Sidebar}</div>
        </>
      )}

      {/* Main content */}
      <div className="md:ml-[17rem] transition-[margin] duration-200">
        {/* Mobile top bar */}
        <header className="md:hidden sticky top-0 z-30 flex items-center gap-3 border-b border-slate-200 bg-white/90 backdrop-blur-sm px-4 py-3 shadow-sm">
          <button type="button" onClick={() => setMobileSidebarOpen(true)} className="rounded-xl p-2 text-slate-600 hover:bg-slate-100">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Devello</p>
            <p className="text-sm font-bold text-slate-900 leading-none">Team Portal</p>
          </div>
        </header>

        <div className="app-main-surface min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50/20 px-4 py-6 sm:px-6 sm:py-8 lg:px-10 lg:py-10">
          {view === 'settings' ? <SettingsView /> : <ProjectCards />}
        </div>
      </div>

      {/* ProjectDetailWorkspace overlay — same as contractor */}
      {detailProjectId && projects.some((p) => String(p.id) === detailProjectId) && (
        <ProjectDetailWorkspace
          user={session?.user}
          project={projects.find((p) => String(p.id) === detailProjectId)}
          expenses={detailProjectExpenses}
          isReadOnly={false}
          onClose={() => setDetailProjectId(null)}
          onPatchProject={handleTeamPatch}
          getProjectTotalExpenses={getProjectTotalExpenses}
          saveExpense={teamSaveExpense}
          deleteExpense={teamDeleteExpense}
          permittedTabIds={getPermittedTabIds(detailProjectId)}
          sectionPermissions={permissions[String(detailProjectId)] || null}
          addedByName={member?.full_name || member?.name || member?.email || null}
          updateExpense={teamUpdateExpense}
          storageBucket="team-project-files"
          storageRoot="team-uploads"
          showTeamAccess={false}
        />
      )}
    </div>
  );
};

export default TeamMemberDashboard;
